import { useState } from "react";

import { toDate, toRelativeDate, isSameDate, toISODateTime } from "common/utils/dates";
import { UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { useUpdateSubscriptionMutation } from "../../hooks";
import { ClientSubscription, ListSubscription } from "../../types";
import SubscriptionDateBanners from "../SubscriptionDateBanners";

export type SubscriptionNextOrderDateUpdateDrawerProps = {
  subscription: ClientSubscription | ListSubscription;
};

const SubscriptionNextOrderDateUpdateDrawer = ({
  subscription,
}: SubscriptionNextOrderDateUpdateDrawerProps) => {
  const { close } = useCurrentDrawer();
  const {
    mutate: updateSubscription,
    isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const currentDate = new Date(subscription.next_order_date);
  const [nextOrderDate, setNextOrderDate] = useState<Date>(currentDate);
  const isSameDateSelected = nextOrderDate ? isSameDate(currentDate, nextOrderDate) : false;

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update next order"
        description={`Use the calendar below to select your next order date. Currently, you will be receiving this item on ${toDate(
          subscription.next_order_date,
        )} (${toRelativeDate(subscription.next_order_date)}).`}
        onClose={close}
      />
      <DrawerLayout.Form
        submitText="Update order"
        canSubmit={!isSameDateSelected}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Modify,
            next_order_date: toISODateTime(nextOrderDate ?? new Date()),
          })
        }
        isLoading={isLoading}
        error={error}
        fieldErrors={[error?.errors.action, error?.errors.next_order_date]}
      >
        <DrawerLayout.Form.Group>
          <Calendar
            disabledDays={{ before: new Date() }}
            selectedDays={nextOrderDate}
            modifiers={{ previous: currentDate }}
            onSelect={setNextOrderDate}
          />
        </DrawerLayout.Form.Group>
        {!isSameDateSelected && (
          <DrawerLayout.Form.Group>
            <DrawerLayout.Form.Label>Your next order date will now be:</DrawerLayout.Form.Label>
            <SubscriptionDateBanners dates={[nextOrderDate]} />
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionNextOrderDateUpdateDrawer;
